import type { Recurso } from '../../api/ops/pagos';
import type { FotoSala, Posicion, RecursoEnVivo, ReservaEnVivo, Reten } from '../../api/ops/sala';
import type { EstadoChip } from '../../componentes/Chip';
import { horaEn } from '../reservas/fechas';
import { MARGEN_RETRASO_MIN } from '../reservas/textos';

/**
 * Reglas de la Sala en vivo (8.7) sin pantalla, para probarlas solas. La foto (`getFloorLiveState`) da el estado de cada
 * recurso colocado; la capacidad, la zona y si está cerrado se leen de Reservas (`listResources`, `active_closure`, G464).
 */

/** Lo que se pinta en cada recurso del plano: Libre, la hora de la próxima, Tarde, Sentada, Retén o Cerrado. */
export type EstadoPintado = 'libre' | 'reservado' | 'tarde' | 'sentado' | 'reten' | 'cerrado';

export const PERMISOS_SALA = {
    ver: 'sala.operativa.ver',
    retenes: 'sala.retenes.gestionar',
    plano: 'sala.plano.ver',
    asignar: 'reservas.recursos.asignar',
    crear: 'reservas.reservas.crear',
    desplazar: 'reservas.reservas.desplazar',
    forzar: 'reservas.reservas.forzar_capacidad',
    llegada: 'pases.puerta.validar',
    deshacerLlegada: 'pases.llegadas.deshacer',
    clientes: 'clientes.fichas.ver',
    reservas: 'reservas.reservas.ver',
} as const;

/** Estados de una reserva que aún esperan en la puerta (la lista «Por llegar»). */
export const POR_LLEGAR = ['pending_payment', 'confirmed'] as const;

/** Lo que ya no ocupa nada (no sale en la lista ni en la hoja). */
export const FUERA = ['cancelled', 'expired', 'refunded', 'no_show', 'completed'] as const;

export function esPorLlegar(r: Pick<ReservaEnVivo, 'status'>): boolean {
    return (POR_LLEGAR as readonly string[]).includes(r.status);
}

/**
 * G849: una reserva con el pago pendiente aún no tiene pase, pero se sienta igual (la mesa queda asignada) para no parar el
 * servicio; el aviso breve (texto del dibujo 8.7, G893) pide avisar al encargado (no hay un aviso de la campana para esto, ver el informe).
 */
export function textoSentadaSinPagar(nombre: string): string {
    return `${nombre}, sentada. Tiene el pago pendiente y aún no tiene pase: avisa al encargado.`;
}

export function sigueViva(r: Pick<ReservaEnVivo, 'status'>): boolean {
    return !(FUERA as readonly string[]).includes(r.status);
}

/** ¿Llega tarde? Por llegar y pasado el margen de su hora de inicio (G322, el mismo que «Con retraso» de 8.1). */
export function llegaTarde(r: Pick<ReservaEnVivo, 'status' | 'starts_at'>, ahora: Date): boolean {
    return esPorLlegar(r) && new Date(r.starts_at).getTime() + MARGEN_RETRASO_MIN * 60_000 <= ahora.getTime();
}

/** El chip de una fila de la lista: solo cuando hay que hacer algo con ella. */
export function chipDeReserva(r: ReservaEnVivo, ahora: Date): { texto: string; estado: EstadoChip } | null {
    if (r.needs_relocation === true) {
        return { texto: 'Para recolocar', estado: 'problem' };
    }
    if (llegaTarde(r, ahora)) {
        return { texto: 'Tarde', estado: 'problem' };
    }
    if (r.assignment_status === 'pending_join') {
        return { texto: 'Juntar reservados', estado: 'pending' };
    }
    if (r.assignment_status === 'unassigned' || (r.resource_ids ?? []).length === 0) {
        return { texto: 'Sin reservado', estado: 'pending' };
    }
    if (r.status === 'pending_payment') {
        return { texto: 'Pendiente de pago', estado: 'pending' };
    }
    if (r.status === 'arrived') {
        return { texto: 'Sentada', estado: 'confirmed' };
    }
    return null;
}

/** ¿Hay que darle sitio? Sin recurso, por juntar o para recolocar. */
export function sinSitio(r: ReservaEnVivo): boolean {
    return r.needs_relocation === true || r.assignment_status === 'pending_join' || r.assignment_status === 'unassigned' || (r.resource_ids ?? []).length === 0;
}

/** La lista de la izquierda: lo que sigue vivo, por hora; «Por llegar» o «Sin reservado» si se filtra. */
export function listaDeLlegadas(foto: FotoSala, filtro: 'todas' | 'por-llegar' | 'sin-reservado', busqueda = '', nombres: Readonly<Record<string, string>> = {}): ReservaEnVivo[] {
    const q = busqueda.trim().toLocaleLowerCase('es');
    return foto.reservations
        .filter(sigueViva)
        .filter((r) => (filtro === 'por-llegar' ? esPorLlegar(r) : filtro === 'sin-reservado' ? sinSitio(r) : true))
        .filter((r) => q === '' || (r.reference ?? '').toLocaleLowerCase('es').includes(q) || (nombres[r.customer_id ?? ''] ?? '').toLocaleLowerCase('es').includes(q))
        .sort((a, b) => a.starts_at.localeCompare(b.starts_at));
}

/** Lo que se pinta en un recurso: cerrado (Reservas) manda; después el estado de la foto. */
export function estadoPintado(vivo: RecursoEnVivo | undefined, recurso: Recurso | undefined, foto: FotoSala, ahora: Date): EstadoPintado {
    if (recurso?.active_closure != null) {
        return 'cerrado';
    }
    switch (vivo?.state) {
        case 'seated':
            return 'sentado';
        case 'held_for_door':
            return 'reten';
        case 'reserved': {
            const r = foto.reservations.find((x) => x.reservation_id === vivo.current_reservation_id);
            return r !== undefined && llegaTarde(r, ahora) ? 'tarde' : 'reservado';
        }
        default:
            return 'libre';
    }
}

/** Cifras de la leyenda (libres, reservadas, sentadas, retenes) de los recursos de una sala del plano. */
export function cifrasLeyenda(estados: readonly EstadoPintado[]): { libres: number; reservadas: number; sentadas: number; retenes: number } {
    return {
        libres: estados.filter((e) => e === 'libre').length,
        reservadas: estados.filter((e) => e === 'reservado' || e === 'tarde').length,
        sentadas: estados.filter((e) => e === 'sentado').length,
        retenes: estados.filter((e) => e === 'reten').length,
    };
}

/** ¿Cabe el grupo en el recurso? (su capacidad de hoy, `current_terms`). */
export function cabe(recurso: Recurso, personas: number): boolean {
    return personas >= recurso.current_terms.min_capacity && personas <= recurso.current_terms.max_capacity;
}

/**
 * Los recursos libres donde sentar a un grupo de N (walk-in o reserva sin sitio): abiertos, libres ahora en la foto y
 * con capacidad para N, de menor a mayor capacidad. Los retenidos no salen: para sentar en ellos se fuerza (G462).
 */
export function libresPara(foto: FotoSala, recursos: readonly Recurso[], personas: number): Recurso[] {
    const enVivo = new Map(foto.resources.map((x) => [x.resource_id, x]));
    return recursos
        .filter((rc) => rc.status === 'active' && rc.active_closure == null && cabe(rc, personas))
        .filter((rc) => {
            const v = enVivo.get(rc.id);
            return v === undefined || v.state === 'free' || (v.state === 'reserved' && v.current_reservation_id == null);
        })
        .sort((a, b) => a.current_terms.max_capacity - b.current_terms.max_capacity || (a.code ?? '').localeCompare(b.code ?? '', 'es', { numeric: true }));
}

/** Suma de capacidades de varios recursos juntados (unión improvisada, G396). */
export function capacidadJunta(recursos: readonly Recurso[]): number {
    return recursos.reduce((n, rc) => n + rc.current_terms.max_capacity, 0);
}

/** ¿Se pueden juntar? Todos unibles y de la misma zona. */
export function sePuedenJuntar(recursos: readonly Recurso[]): boolean {
    if (recursos.length < 2) {
        return true;
    }
    const zona = recursos[0]?.current_terms.zone_id;
    return recursos.every((rc) => rc.current_terms.joinable && rc.current_terms.zone_id === zona);
}

/** Las reservas que hay que desplazar para sentar en estos recursos: las que los ocupan ahora y no son esta. */
export function aDesplazar(foto: FotoSala, recursos: readonly string[], reservaId: string | null): ReservaEnVivo[] {
    const ids = new Set(
        foto.resources
            .filter((x) => recursos.includes(x.resource_id) && x.current_reservation_id != null && x.current_reservation_id !== reservaId)
            .map((x) => x.current_reservation_id as string),
    );
    return foto.reservations.filter((r) => ids.has(r.reservation_id));
}

/** El retén vivo de un recurso, si lo hay. */
export function retenDe(foto: FotoSala, recursoId: string): Reten | undefined {
    return foto.door_holds.find((h) => h.resource_id === recursoId && h.status === 'live');
}

/** «hace 10 min» o «en 20 min» respecto a ahora. */
export function relativo(instante: string, ahora: Date): string {
    const min = Math.round((new Date(instante).getTime() - ahora.getTime()) / 60_000);
    if (min === 0) {
        return 'ahora';
    }
    return min > 0 ? `en ${String(min)} min` : `hace ${String(-min)} min`;
}

/** Caja que ocupan las posiciones de una sala, para escalar el plano al hueco disponible. */
export function cajaDe(posiciones: readonly Pick<Posicion, 'x' | 'y' | 'width' | 'height'>[], ancho: number, alto: number): { ancho: number; alto: number } {
    const derecha = Math.max(ancho, ...posiciones.map((p) => p.x + p.width));
    const abajo = Math.max(alto, ...posiciones.map((p) => p.y + p.height));
    return { ancho: Math.max(1, derecha), alto: Math.max(1, abajo) };
}

/** Hora local «HH:MM» en un día y zona → instante ISO (para «Hasta» del retén). Sin librerías: prueba los desfases ±14 h. */
export function instanteLocal(dia: string, hora: string, zona: string): string {
    const [h, m] = hora.split(':').map(Number);
    const base = Date.parse(`${dia}T00:00:00Z`) + ((h ?? 0) * 60 + (m ?? 0)) * 60_000;
    const formato = new Intl.DateTimeFormat('en-CA', { timeZone: zona, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
    for (let desfase = -14 * 60; desfase <= 14 * 60; desfase += 15) {
        const t = base - desfase * 60_000;
        const partes = Object.fromEntries(formato.formatToParts(new Date(t)).map((p) => [p.type, p.value]));
        if (`${String(partes.year)}-${String(partes.month)}-${String(partes.day)}` === dia && `${String(partes.hour)}:${String(partes.minute)}` === hora) {
            return new Date(t).toISOString();
        }
    }
    return new Date(base).toISOString();
}

/** Horas para «Hasta» del retén: cada 30 min desde la próxima media hora, hasta 12 horas (la API corta al fin de la jornada). */
export function horasDeReten(ahora: Date, zona: string): { valor: string; texto: string }[] {
    const paso = 30 * 60_000;
    const primero = Math.ceil((ahora.getTime() + 60_000) / paso) * paso;
    return Array.from({ length: 24 }, (_, i) => {
        const t = new Date(primero + i * paso);
        return { valor: t.toISOString(), texto: horaEn(t, zona) };
    });
}

